import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { BookingDashboardDevice, BookingStore } from '@store';
import { SubscriptionPlan } from '@vi-iot/client';
import { Observable, Subject } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-booking-voucher-devices',
  templateUrl: './booking-voucher-devices.component.html',
  styleUrls: ['./booking-voucher-devices.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingVoucherDevicesComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  @Output() nextPage = new EventEmitter<void>();
  @Output() navigateToCheckout = new EventEmitter();
  @Output() back = new EventEmitter<void>();

  selectedPlan: SubscriptionPlan;
  searchTerm: string = null;
  isAnyInstallationSelected: boolean;
  maxSelectedDevices: number;
  isMaxSelectedDevicesReached = false;
  selectedInstallationsCount: number;
  readonly BookingPlans = SubscriptionPlan;
  readonly MAX_SELECTED_DEVICES = 10;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public bookingStore: BookingStore,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.bookingStore.selectedPlan$.pipe(first()).subscribe((plan) => {
      this.selectedPlan = plan;
    });

    if (this.selectedPlan === SubscriptionPlan.Trial) {
      this.bookingStore.trialSubscriptions$.pipe(first()).subscribe((trialSubscriptions) => {
        this.maxSelectedDevices = trialSubscriptions?.remaining || this.MAX_SELECTED_DEVICES;
      });
    } else {
      this.maxSelectedDevices = 1;
    }

    this.bookingStore.selectedDevices$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((installations) => {
        this.isAnyInstallationSelected = installations.length > 0;
      });

    this.bookingStore.selectedInstallationsCount$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedInstallationsCount) => {
        this.selectedInstallationsCount = selectedInstallationsCount;

        this.isMaxSelectedDevicesReached =
          this.selectedInstallationsCount >= this.maxSelectedDevices;
      });
  }

  search(text?: string): void {
    if (this.searchTerm === text) {
      return;
    }
    this.searchTerm = text;
    this.bookingStore.setBookingPaginationPage(0);
    this.bookingStore.setSearchInputValue(text);
  }

  isDisabledForVoucher(installation: BookingDashboardDevice): Observable<boolean> {
    return this.bookingStore
      .isSelectedInstallation(installation.vin)
      .pipe(
        map(
          (isSelected) =>
            (this.isMaxSelectedDevicesReached && !isSelected) || !installation.isVoucherAvailable
        )
      );
  }

  isSelectedInstallation(installation: BookingDashboardDevice): Observable<boolean> {
    return this.bookingStore.isSelectedInstallation(installation.vin);
  }

  onInstallationSelect(installation: BookingDashboardDevice): void {
    this.bookingStore.toggleInstallationSelection(installation.vin);
  }

  onPageChange(paginator: PageEvent): void {
    const { pageIndex } = paginator;
    const nextPage = pageIndex > -1 ? pageIndex : 0;
    this.bookingStore.setBookingPaginationPage(nextPage);
    window.scrollTo(0, 0);
  }

  onContinue(): void {
    this.clearQueryParams();
    this.nextPage.emit();
  }

  private clearQueryParams(): void {
    const params = { ...this.activatedRoute.snapshot.queryParams };
    delete params.selectedStep;
    delete params.boilerSerial;
    delete params.update;

    this.router.navigate([], { queryParams: params });
  }

  onBack(): void {
    this.back.emit();
  }

  nextStep(): void {
    this.navigateToCheckout.emit();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.bookingStore.setBookingPaginationPage(0);
    this.bookingStore.setSearchInputValue(null);
  }
}
